import { useEffect, useMemo, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { Link, useParams, useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, Plus, ShieldCheck, Clock, Ban, Copy, Timer, Users, KeyRound, RotateCcw, Activity, ShoppingCart, Receipt, ScrollText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { isFromAdminFestivals, clearAdminFestivalEntry } from "@/lib/adminNav";

// Iter371 · Fix 3 — Map each helper role to its bolt-on pass key so
// the "Buy extra slot" modal can look up the current unit price from
// `/festival/pricing`. Kept in sync with `_DEFAULT_PRICING` in
// `routes_festival.py`.
const ROLE_TO_BOLTON = {
  check_in_helper: "bolton_helper_seat",
  court_ops_helper: "bolton_ops_seat",
  deputy_helper: "bolton_deputy_seat",
  // Iter459 — OPS Assist (`roster_ops_helper`) is now a first-class
  // Purchase Summary role; its bolt-on `bolton_roster_ops_seat`
  // mirrors the other extra-seat kinds.
  roster_ops_helper: "bolton_roster_ops_seat",
};

// Iter387 — Owner audit panel visible on organiser's Purchase Summary.
function OwnerAuditPanel({ festivalId }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!festivalId) return;
    (async () => {
      try {
        const r = await api.get(`/festival/${festivalId}/admin-audits`);
        setItems(r.data.items || []);
      } catch { setItems([]); }
    })();
  }, [festivalId]);
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-4 border border-[#FFB020] bg-[#4B3F0F]/30 p-3" data-testid="owner-audit-panel">
      <div className="flex items-center gap-2 mb-2">
        <ScrollText className="w-3.5 h-3.5 text-[#FFB020]" />
        <div className="label-mono text-[#FFB020] text-[10px]">OWNER CHANGES ({items.length})</div>
      </div>
      <div className="space-y-1 max-h-48 overflow-y-auto text-xs">
        {items.slice(0, 20).map((a) => (
          <div key={a.id} data-testid={`owner-audit-${a.id}`} className="flex items-baseline justify-between gap-2">
            <div>
              <b className="text-white">{a.action}</b>
              <span className="text-[10px] text-[#A3A3A3] ml-2">by {a.actor_name || a.actor_email || "Rally-Lab"}</span>
            </div>
            <span className="text-[10px] text-[#525252] shrink-0">{a.created_at?.slice(0, 16).replace("T", " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function formatMoney(amount, currency) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (currency || "gbp").toUpperCase(),
    }).format(amount || 0);
  } catch {
    return `${amount} ${currency || ""}`;
  }
}

import { useAuth } from "@/context/AuthContext";
import { useFestivalReadOnly } from "@/lib/useFestivalReadOnly";
import FestivalEndedBanner from "@/components/FestivalEndedBanner";
import { HelperRolePicker } from "@/components/HelperRolePicker";
import { SHIFT_OPTIONS } from "@/components/HelperPassQuickModal";

// Iter326 · Bug 6 — After a brute-force reset or "Reset older sessions"
// the backend rotates the 8-digit access code + bumps
// `token_generation`. We cache the fresh code in sessionStorage keyed
// by pass_id so the organiser can hover over the helper's name to
// re-reveal the code + QR (rather than losing it when the toast dismisses).
// The cache stores the `gen` it was minted at; if the pass's live
// `token_generation` has moved past it (helper re-redeemed, code
// rotated again) we invalidate the entry.
const FRESH_CODE_KEY = (pid) => `rl_helper_fresh_code_${pid}`;
function readFreshCode(pid) {
  try {
    const raw = window.sessionStorage.getItem(FRESH_CODE_KEY(pid));
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}
function writeFreshCode(pid, entry) {
  try { window.sessionStorage.setItem(FRESH_CODE_KEY(pid), JSON.stringify(entry)); } catch {}
}
function clearFreshCode(pid) {
  try { window.sessionStorage.removeItem(FRESH_CODE_KEY(pid)); } catch {}
}

// Iter326 · Bug 2 — `festival_only_helper` (legacy "Festival Deputy"
// label) is no longer surfaced in the Create-a-Pass dropdown. Existing
// passes with that role still render (map retained for the label
// lookup); new passes are minted as `deputy_helper` only.
const ROLE_META = {
  check_in_helper: { label: "Check-In Only", color: "#66C2FF", scope: "Names + tap check-in + player QR" },
  court_ops_helper: { label: "Court Operations", color: "#CCFF00", scope: "Generate games, tick statuses, drag/drop + check-in" },
  deputy_helper: { label: "Deputy", color: "#B26BFF", scope: "OPS + Player Manager (roster, ratings, import) — blocked from billing/settings" },
  festival_only_helper: { label: "Deputy", color: "#B26BFF", scope: "OPS + Player Manager (roster, ratings, import) — blocked from billing/settings" },
  // Iter431/437 — OPS Assist helper (internal name unchanged).
  roster_ops_helper: { label: "OPS Assist", color: "#FFB020", scope: "Registration + Resting roster only — mark Available, tap Assign to Coaching (right-click on desktop)" },
};
const CREATE_ROLES = ["check_in_helper", "court_ops_helper", "roster_ops_helper", "deputy_helper"];

const CAP_MAX = 5;

function fmtDeviceCap(cap) {
  // Iter373 · Fix 2 — Cap is always a numeric 1–6 now (default 1).
  // Owner may adjust the festival-wide limit; individual pass rows
  // just render the cap they were minted with.
  if (cap == null) return "1 device";
  const n = Number(cap);
  if (!Number.isFinite(n) || n <= 0) return "1 device";
  return `${n} device${n === 1 ? "" : "s"}`;
}

function fmtRemaining(iso) {
  try {
    const ms = new Date(iso).getTime() - Date.now();
    if (ms <= 0) return "expired";
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${h}h ${m}m left`;
  } catch { return ""; }
}

export default function FestivalHelpers() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  // Iter372 · Fix 2 — Rally-Lab platform owner controls the device
  // limit for a pass. Organisers + deputies never see the setting.
  const isPlatformOwner = user?.role === "admin"
    || (user?.permissions || []).some((p) => p === "admin" || p === "super_admin");
  const [passes, setPasses] = useState([]);
  const passCtl = useListControls(passes, { name: (p) => p.label, fields: (p) => [p.label, p.role, p.code], initialSort: "default" });
  const [quota, setQuota] = useState(null);
  const [busy, setBusy] = useState(false);
  const [festival, setFestival] = useState(null);
  const [pricing, setPricing] = useState(null);
  // Iter391 · Fix 3 — Roster player picker replaces the free-text
  // label input on the create-a-pass form. Loaded from /players so
  // helper passes are always linked to a real festival player.
  const [players, setPlayers] = useState([]);
  // Iter391 · Fix 5 — Deputy on starter tier can't buy add-on seats.
  const [festRole, setFestRole] = useState({ is_owner: true, role: "owner" });
  const cannotAddHelpers = ["check_in_helper", "court_ops_helper", "roster_ops_helper"].includes(festRole?.role);
  const [form, setForm] = useState({ role: "check_in_helper", player_id: "", label: "", hours: 4 });
  const [freshPass, setFreshPass] = useState(null); // { pass_id, code, redeem_url, role, label, expires_at }
  // Iter326 · Bug 6 — Track which pass label is being hovered so we
  // render the QR + fresh-code tooltip beside it. `null` means no reveal.
  const [hoveredCodePid, setHoveredCodePid] = useState(null);
  // Iter371 · Fix 2 — Always-on shift-pass QR hover for active/expired
  // (non-revoked) rows. Separate from `hoveredCodePid` (fresh-code
  // reset reveal) so the two behaviours can co-exist.
  const [hoveredQrPid, setHoveredQrPid] = useState(null);
  // Iter371 · Fix 3 — Buy-extra-slot modal state. Role + qty +
  // running total. Continue posts to /festival-basket/checkout.
  const [buyRole, setBuyRole] = useState(null);
  const [buyQty, setBuyQty] = useState(1);
  const [buyBusy, setBuyBusy] = useState(false);
  const [extendMenuPid, setExtendMenuPid] = useState(null);
  const backendOrigin = useMemo(() => (process.env.REACT_APP_BACKEND_URL || "").replace(/\/api\/?$/, ""), []);
  // Iter433 · Fix — Every player dropdown in Shift Passes is sorted
  // alphabetically by last name then first name (case-insensitive).
  const [helperPlayerQuery, setHelperPlayerQuery] = useState("");
  const sortedPlayers = useMemo(() => {
    const key = (p) => `${(p.first_name || "").toLowerCase()} ${(p.last_name || "").toLowerCase()}`.trim();
    const q = helperPlayerQuery.trim().toLowerCase();
    return [...(players || [])]
      .filter((p) => !q || key(p).includes(q))
      .sort((a, b) => key(a).localeCompare(key(b), "en-GB"));
  }, [players, helperPlayerQuery]);

  const load = async () => {
    const pre = await api.get(`/festival/${id}/my-role`).catch(() => null);
    if (["check_in_helper", "court_ops_helper", "roster_ops_helper"].includes(pre?.data?.role)) {
      setFestRole(pre.data);
      return;
    }
    const [p, f, q, pr, pl, mr] = await Promise.all([
      api.get(`/festival/${id}/helpers/passes`),
      api.get(`/festival/${id}`),
      api.get(`/festival/${id}/helpers/quota`).catch(() => ({ data: null })),
      api.get(`/festival/pricing`).catch(() => ({ data: null })),
      // Iter391 · Fix 3 — Roster for the player picker on create-a-pass.
      api.get(`/festival/${id}/players`).catch(() => ({ data: { items: [] } })),
      // Iter391 · Fix 5 — Caller's festival role (deputy vs owner) so
      // the deputy-on-starter buy gate can hide the button.
      api.get(`/festival/${id}/my-role`).catch(() => ({ data: { is_owner: true, role: "owner" } })),
    ]);
    setPricing(pr?.data?.pricing || null);
    setPlayers(pl?.data?.items || []);
    setFestRole(mr?.data || { is_owner: true, role: "owner" });
    const items = p.data.items || [];
    // Iter326 · Bug 6 — Invalidate cached fresh codes whose
    // `token_generation` is behind the pass's current gen (the helper
    // re-redeemed → code has already rotated again, so the cached
    // code is stale).
    for (const p2 of items) {
      const fc = readFreshCode(p2.id);
      if (fc && fc.gen != null && Number(fc.gen) < Number(p2.token_generation || 0)) {
        clearFreshCode(p2.id);
      }
    }
    setPasses(items);
    setFestival(f.data);
    setQuota(q?.data || null);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Iter459 · Fix 2 — Poll the Purchase Summary quota so admin-
  // granted overrides (via the Festival Toolkit) show up for the
  // organiser without a page refresh. 15 s is slow enough to keep
  // the request budget tiny and fast enough that an admin applying
  // an override while the organiser is on the page sees the count
  // update within a tick. Only pings when the tab is visible.
  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    const ping = async () => {
      if (!alive) return;
      if (typeof document !== "undefined" && document.hidden) return;
      try {
        const q = await api.get(`/festival/${id}/helpers/quota`);
        if (alive) setQuota(q?.data || null);
      } catch { /* silent — kickout handled by interceptor */ }
    };
    const iv = setInterval(ping, 15000);
    const onVis = () => ping();
    window.addEventListener("visibilitychange", onVis);
    window.addEventListener("focus", onVis);
    return () => {
      alive = false;
      clearInterval(iv);
      window.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("focus", onVis);
    };
  }, [id]);

  const createPass = async () => {
    // Iter462 — Post-festival read-only lock. Reuses the shared
    // `isReadOnly` hook so Shift Passes block pass creation
    // identically to Player Manager (Iter461) and Live Ops.
    if (isReadOnly) {
      toast.info("This festival has ended. Please request an extension to make changes.");
      return;
    }
    // Iter433 · Fix — player linkage is optional now. Volunteers /
    // walk-in helpers who aren't on the festival roster still need
    // shift passes; require a typed label instead of forcing a
    // roster pick.
    if (!form.label.trim()) { toast.error("Enter a label / name for the pass"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/festival/${id}/helpers/passes`, {
        role: form.role, label: form.label.trim(), hours: form.hours, until_end: form.hours === 0,
        player_id: form.player_id || null,
      });
      setFreshPass(r.data);
      setForm({ role: "check_in_helper", player_id: "", label: "", hours: 4 });
      toast.success("Pass created — hand the code to your helper");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to create");
    } finally { setBusy(false); }
  };

  const extend = async (pid, hoursOverride) => {
    if (isReadOnly) {
      toast.info("This festival has ended. Please request an extension to make changes.");
      return;
    }
    // Iter367 · Fix 5 — Extend supports a preset dropdown (1/2/4/24h
    // + "until end of festival"). Backend already handles expired
    // passes (base = max(cur_exp, now)); the frontend now surfaces
    // the button regardless of state and caps the end at the
    // festival's stored `ends_at` when the "until end" option is
    // chosen.
    let h = hoursOverride;
    if (h === "until_end") {
      const festEnds = festival?.ends_at ? new Date(festival.ends_at) : null;
      if (!festEnds) { toast.error("Festival end time not set"); return; }
      h = Math.max(1, Math.ceil((festEnds.getTime() - Date.now()) / 3600000));
    }
    if (!Number.isFinite(h) || h < 1) return;
    // Cap the extension at the festival's end so an organiser can't
    // push a pass past the event.
    // Iter391 · Fix 6 — Floor cap at 0 (not 1) so an already-ended
    // festival doesn't silently grant a one-hour extension. Bail with
    // a toast when there's no headroom left.
    if (festival?.ends_at) {
      const capH = Math.max(0, Math.ceil((new Date(festival.ends_at).getTime() - Date.now()) / 3600000));
      if (capH === 0) {
        toast.error("Festival has already ended — can't extend");
        return;
      }
      h = Math.min(h, capH);
    }
    try {
      await api.post(`/festival/${id}/helpers/passes/${pid}/extend`, { hours: h });
      toast.success(`Extended by ${h}h`);
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Extend failed"); }
  };

  const revoke = async (pid) => {
    if (isReadOnly) {
      toast.info("This festival has ended. Please request an extension to make changes.");
      return;
    }
    if (!window.confirm("Revoke this pass? The helper is signed out on their next request.")) return;
    try {
      await api.post(`/festival/${id}/helpers/passes/${pid}/revoke`);
      toast.success("Revoked");
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Revoke failed"); }
  };

  const unlock = async (pid) => {
    try {
      const r = await api.post(`/festival/${id}/helpers/passes/${pid}/unlock`);
      // Iter326 · Bug 6 — Unlock now also rotates the 8-digit code
      // (the old one was partially guessed). Persist the fresh code
      // to sessionStorage so the organiser can hover the helper's
      // name in the Shift Passes list to reveal QR + code again.
      const newCode = r.data?.new_code;
      const newGen = r.data?.token_generation;
      if (newCode) {
        writeFreshCode(pid, { code: newCode, gen: newGen ?? null, at: Date.now() });
        toast.success(`Lock reset — new access code: ${newCode}`, { duration: 12000 });
      } else {
        toast.success("Lock reset — helper can try again");
      }
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Unlock failed"); }
  };

  // Iter312 — Rotate the code + generation without revoking the pass so
  // any device holding an older token gets bumped off but the legit
  // helper can re-redeem straight away.
  const revokeOlder = async (pid) => {
    if (isReadOnly) {
      toast.info("This festival has ended. Please request an extension to make changes.");
      return;
    }
    if (!window.confirm("Reset older sessions? Anyone currently redeemed will be signed out; the legit helper will need to re-scan the QR + type the new code.")) return;
    try {
      const r = await api.post(`/festival/${id}/helpers/passes/${pid}/revoke-older`);
      // Show the fresh code inline so the organiser can hand it back to the
      // trusted helper right away — this pass has no way to reveal it later.
      // Iter326 · Bug 6 — Also cache the code in sessionStorage so the
      // hover-reveal on the Shift Passes list keeps working after the
      // toast dismisses.
      const newCode = r.data?.new_code;
      const newGen = r.data?.token_generation;
      if (newCode) {
        writeFreshCode(pid, { code: newCode, gen: newGen ?? null, at: Date.now() });
      }
      toast.success(`Older sessions cleared. New access code: ${newCode || ""}`, { duration: 12000 });
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Reset failed"); }
  };

  // Iter312 — Bump the concurrent-device cap on a live pass (e.g. an
  // event grew a shared desk device). Kept simple with a prompt.
  const updateCap = async (pid, currentCap) => {
    // Iter372 · Fix 2 — Rally-Lab platform owner only. Prompt takes
    // 1-5 or "unlimited" (blank/0 clears the cap).
    const raw = window.prompt(
      `Device cap for this pass? Enter 1–${CAP_MAX}, or leave blank to use the role default.`,
      currentCap == null ? "" : String(currentCap),
    );
    if (raw === null) return; // cancelled
    const trimmed = raw.trim();
    let body;
    if (!trimmed) {
      body = { max_concurrent_redeems: null };
    } else {
      const n = Number(trimmed);
      if (!Number.isFinite(n) || n < 1 || n > CAP_MAX) {
        toast.error(`Enter a number between 1 and ${CAP_MAX}, or leave blank to use the role default.`);
        return;
      }
      body = { max_concurrent_redeems: n };
    }
    try {
      await api.patch(`/festival/${id}/helpers/passes/${pid}`, body);
      toast.success(body.max_concurrent_redeems == null
        ? "Device cap reset to the role default"
        : `Device cap set to ${body.max_concurrent_redeems}`);
      await load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Update failed"); }
  };

  const copyText = (v) => {
    navigator.clipboard?.writeText(v).then(() => toast.success("Copied"), () => window.prompt("Copy this", v));
  };

  // Iter371 · Fix 3 — Buy-extra-slot helpers. Opens the modal for a
  // specific role and submits a single-line basket-checkout to
  // Stripe. `helper_seats_bought[role]` is bumped by the webhook /
  // status-poll fulfilment path so quota ceilings update after
  // Stripe returns.
  const openBuyModal = (role) => {
    if (isReadOnly) {
      toast.info("This festival has ended. Please request an extension to make changes.");
      return;
    }
    setBuyRole(role);
    setBuyQty(1);
  };
  const closeBuyModal = () => {
    setBuyRole(null);
    setBuyQty(1);
  };
  // Iter462 — Post-festival read-only gate. Reuses the shared hook so
  // Shift Passes locks identically to Player Manager + Live Ops. Must
  // be called BEFORE the `if (!festival) return` early-exit below so
  // the hook order stays consistent across renders.
  const { isReadOnly } = useFestivalReadOnly(festival);
  const buyBoltonKey = buyRole ? ROLE_TO_BOLTON[buyRole] : null;
  const buyDef = buyBoltonKey && pricing ? pricing[buyBoltonKey] : null;
  const buyUnitAmount = buyDef ? Number(buyDef.amount) || 0 : 0;
  const buyCurrency = buyDef?.currency || "gbp";
  const buyCanBuy = !!buyDef && !buyDef.poa && buyUnitAmount > 0;
  const buyTotal = Math.round(buyUnitAmount * Math.max(1, Number(buyQty) || 1) * 100) / 100;
  const submitBuy = async () => {
    if (isReadOnly) {
      toast.info("This festival has ended. Please request an extension to make changes.");
      return;
    }
    if (!buyCanBuy || !buyBoltonKey) return;
    setBuyBusy(true);
    try {
      const { data } = await api.post(`/festival-basket/checkout`, {
        items: [{ pass_type: buyBoltonKey, qty: Math.max(1, Number(buyQty) || 1) }],
        festival_id: id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Checkout failed");
      setBuyBusy(false);
    }
  };

  if (cannotAddHelpers) {
    return (
      <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="helpers-no-permission-page">
        <Ban className="w-8 h-8 text-[#FFB020] mx-auto mb-3" />
        <p className="text-sm text-[#A3A3A3]" data-testid="helpers-no-permission">You don't have permission to add helpers.</p>
      </div>
    );
  }
  if (!festival) return <div className="p-8 text-sm text-[#A3A3A3]">Loading…</div>;

  // Iter317 — Helper passes locked until the festival is fully activated
  // (paid / complimentary / override). Draft + demo modes show the form
  // so organisers can plan, but Generate is disabled with a banner.
  const licenseActive = ["active_paid", "complimentary", "override"].includes(festival?.license?.state);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8" data-testid="festival-helpers-page">
      {/* Iter462 — Banner at the very top so organisers see the lock
          before any purchase or create action is attempted. */}
      {isReadOnly && <FestivalEndedBanner />}
      <div className="flex items-center gap-3 mb-3 flex-wrap">
        {isFromAdminFestivals() && (
          <button
            type="button"
            onClick={() => { clearAdminFestivalEntry(); nav("/admin/festivals"); }}
            data-testid="back-to-festival-manager"
            className="text-[11px] uppercase tracking-wider text-[#CCFF00] hover:text-white inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Festival Manager
          </button>
        )}
        <button
          type="button"
          onClick={() => nav(`/festival/${id}`)}
          className="text-[11px] uppercase tracking-wider text-[#A3A3A3] hover:text-white inline-flex items-center gap-1"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> {festival.name}
        </button>
      </div>
      {/* Iter369 · Fix 3 — Read-only Purchase Summary. Shows pack
           name, purchase date, and per-role included/used/remaining.
           Backed by GET /helpers/quota which is Organiser/Deputy
           gated. Never renders when the licence is inactive or the
           payload is missing (e.g. legacy comp festivals).
           Iter371 · Fix 1 — Also renders an Amount Paid breakdown
           (tier + addons + total) from the same payload. */}
      {/* Iter387 — Owner change log for this festival. Renders any
           entries in admin_festival_audits so the organiser sees
           exactly what the Rally-Lab team altered and when. */}
      <OwnerAuditPanel festivalId={id} />
      {quota && quota.tier && (
        <div
          className="mb-6 border border-[#66C2FF] p-4 bg-[#0F2A4B]/30"
          data-testid="helpers-quota-summary"
        >
          <div className="flex items-baseline justify-between gap-2 mb-3">
            <div>
              <div className="label-mono text-[#66C2FF]">PURCHASE SUMMARY</div>
              <div className="text-base font-bold mt-0.5" data-testid="helpers-quota-pack">
                {quota.pack_name || quota.tier} · {quota.tier?.toUpperCase()}
              </div>
              {quota.purchase_date && (
                <div className="text-[11px] text-[#A3A3A3] mt-0.5" data-testid="helpers-quota-date">
                  Purchased {new Date(quota.purchase_date).toLocaleDateString()}
                </div>
              )}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]" data-testid="helpers-quota-state">
              {quota.license_state}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {(quota.roles || []).map((r) => {
              const boltonKey = ROLE_TO_BOLTON[r.role];
              const boltonDef = boltonKey && pricing ? pricing[boltonKey] : null;
              const boltonCanBuy = !!boltonDef && !boltonDef.poa && Number(boltonDef.amount) > 0;
              // Iter391 · Fix 5 — Deputies can't buy add-on seats on
              // starter tier festivals. Buy button hidden entirely
              // for that combo (deputy sees the quota grid but no
              // upgrade path).
              const isDeputy = festRole?.role === "deputy_helper";
              const isStarterTier = (quota.tier || "").toLowerCase() === "starter";
              const buyGatedForDeputy = isDeputy && isStarterTier;
              return (
                <div
                  key={r.role}
                  className="border border-[#262626] p-3 bg-[#0A0A0A] flex flex-col"
                  data-testid={`helpers-quota-role-${r.role}`}
                >
                  <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">{r.label}</div>
                  <div className="mt-1 text-2xl font-bold">
                    <span data-testid={`helpers-quota-used-${r.role}`}>{r.used}</span>
                    <span className="text-[#525252]"> / {r.ceiling}</span>
                  </div>
                  <div className="text-[11px] mt-1">
                    <span data-testid={`helpers-quota-remaining-${r.role}`} className={r.remaining === 0 ? "text-[#FF4500]" : "text-[#CCFF00]"}>
                      {r.remaining} remaining
                    </span>
                  </div>
                  <div className="text-[10px] text-[#525252] mt-1">
                    {r.included} included{r.bought_extras ? ` + ${r.bought_extras} bought` : ""}
                  </div>
                  {/* Iter459 · Fix 2 — Toolkit admin grants surface as
                       their own line so the organiser can tell at a
                       glance which slots came from Rally-Lab (vs. the
                       tier default or a paid extra). Bought extras
                       stay silent per spec. */}
                  {r.admin_granted > 0 && (
                    <div
                      className="text-[10px] text-[#66C2FF] mt-0.5"
                      data-testid={`helpers-quota-admin-granted-${r.role}`}
                    >
                      {r.admin_granted} granted by admin
                    </div>
                  )}
                  {/* Iter371 · Fix 3 — Buy extra slot for this role.
                       Hidden when the bolt-on isn't priced yet (£0
                       placeholder) — organiser needs Rally-Lab to
                       set a price before the button appears. */}
                  {boltonCanBuy && !buyGatedForDeputy && (
                    <button
                      type="button"
                      onClick={() => openBuyModal(r.role)}
                      data-testid={`helpers-quota-buy-${r.role}`}
                      className="mt-3 inline-flex items-center gap-1 px-2 py-1.5 text-[10px] uppercase tracking-widest border border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black self-start"
                      title={`Add another ${r.label} pass slot`}
                    >
                      <Plus className="w-3 h-3" /> Buy extra · {formatMoney(boltonDef.amount, boltonDef.currency)}
                    </button>
                  )}
                  {buyGatedForDeputy && (
                    <div
                      data-testid={`helpers-quota-buy-locked-${r.role}`}
                      className="mt-3 text-[10px] text-[#525252] italic"
                    >
                      Ask the organiser to upgrade — deputies can't buy on Starter.
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {/* Iter371 · Fix 1 — Amount Paid breakdown. Tier line first,
               then any bolt-on / basket addons, then a bold total. */}
          {(quota.tier_line || (quota.addons || []).length > 0 || Number(quota.total_paid) > 0) && (
            <div className="mt-4 border border-[#525252] bg-[#0F0F0F] p-3" data-testid="helpers-quota-payments">
              <div className="label-mono text-[10px] text-[#A3A3A3] mb-2 flex items-center gap-1">
                <Receipt className="w-3 h-3" /> AMOUNT PAID
              </div>
              {quota.tier_line && (
                <div className="text-xs text-white flex justify-between mb-1" data-testid="helpers-quota-tier-line">
                  <span>
                    {quota.tier_line.label}
                    {quota.tier_line.qty > 1 ? ` × ${quota.tier_line.qty}` : ""}
                  </span>
                  <span>{formatMoney(quota.tier_line.amount, quota.tier_line.currency)}</span>
                </div>
              )}
              {(quota.addons || []).map((a, i) => (
                <div
                  key={`${a.session_id || "x"}-${a.pass_type || "?"}-${i}`}
                  className="text-xs text-white flex justify-between mb-1"
                  data-testid={`helpers-quota-addon-${i}`}
                >
                  <span>
                    {a.label}
                    {a.qty > 1 ? ` × ${a.qty}` : ""}
                  </span>
                  <span>+{formatMoney(a.amount, a.currency)}</span>
                </div>
              ))}
              <div className="text-sm font-bold text-[#CCFF00] flex justify-between border-t border-[#262626] pt-2 mt-2" data-testid="helpers-quota-total-paid">
                <span>Total paid</span>
                <span>{formatMoney(quota.total_paid, quota.currency)}</span>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="mb-6">
        <div className="label-mono text-[#CCFF00] mb-1">FESTIVAL · HELPER PASSES</div>
        <h1 className="h-display text-3xl">Shift passes for helpers</h1>
        <p className="text-sm text-[#A3A3A3] mt-1 max-w-2xl">
          Print a QR + 6-digit code, hand it to your helper, and they scan → type the code → get scoped access for their shift. No accounts needed. Passes are single-festival, revocable, and auto-expire.
        </p>
      </div>

      {/* Iter403 — Coaching View QR poster. Public read-only link
           coaches scan to see who's signed up to their court. */}
      <div className="card-surface p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-5" data-testid="coaching-view-qr-card">
        <div className="shrink-0 bg-white p-3">
          <QRCodeSVG
            value={`${window.location.origin}/coaching/${id}`}
            size={280}
            level="H"
            includeMargin={false}
            shapeRendering="crispEdges"
            style={{ width: 140, height: 140, imageRendering: "pixelated" }}
            data-testid="coaching-view-qr-svg"
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-mono text-[#CCFF00] mb-1 text-[10px]">COACH POSTER · READ-ONLY</div>
          <h2 className="text-lg font-bold text-white leading-tight">Coaching sessions view</h2>
          <p className="text-xs text-[#A3A3A3] mt-1.5 leading-relaxed">
            Print or display this QR so coaches can scan and see their session, court, start time and signed-up players. No login required, auto-refreshes every 30 s, filters sessions older than 30 min.
          </p>
          <a
            href={`/coaching/${id}`}
            target="_blank"
            rel="noreferrer"
            data-testid="coaching-view-open-link"
            className="inline-block mt-3 text-xs uppercase tracking-widest text-[#CCFF00] border-b border-[#CCFF00] hover:opacity-80"
          >
            Open view →
          </a>
        </div>
      </div>

      {/* Create form */}
      <div className="card-surface p-4 mb-6" data-testid="helpers-create-form">
        <div className="label-mono text-[#CCFF00] mb-3">CREATE A PASS</div>
        {!licenseActive && (
          <div
            className="border border-[#FFB020] bg-[#1A140A] p-3 mb-3 flex items-start gap-2"
            data-testid="helpers-locked-banner"
          >
            <Ban className="w-4 h-4 text-[#FFB020] shrink-0 mt-0.5" />
            <div className="text-xs text-white">
              <div className="label-mono text-[#FFB020] text-[10px]">PAYMENT REQUIRED</div>
              Helper passes and QR codes are locked until this festival is activated.
              You can plan labels, roles and shift lengths here — Generate Pass unlocks
              once you buy an activation pass or receive complimentary access.
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-[#525252] block mb-1">
              Pass label / helper name
            </label>
            {/* Iter433 · Fix — Free-text label is the primary field so
                 volunteers who aren't on the festival roster can be
                 given a shift pass. Roster link stays as an optional
                 secondary picker below (alpha-sorted last-name-first). */}
            <input
              type="text"
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
              data-testid="helpers-form-label"
              placeholder="e.g. Alex — Front desk (Sat AM)"
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] focus:outline-none px-3 py-2 text-sm"
            />
            <label className="text-[9px] uppercase tracking-widest text-[#525252] block mt-2 mb-1">
              Link to a player (optional)
            </label>
            <input value={helperPlayerQuery} onChange={(e) => setHelperPlayerQuery(e.target.value)} placeholder="Search players by name…"
              data-testid="helpers-form-player-search"
              className="w-full mb-1 bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] focus:outline-none px-3 py-1.5 text-xs" />
            <select
              value={form.player_id}
              onChange={(e) => {
                const pid = e.target.value;
                const player = players.find((pl) => pl.id === pid);
                // Only auto-fill the label when it's currently empty
                // so a manually-typed label isn't wiped by picking a
                // player.
                const derivedLabel = player
                  ? `${player.first_name || ""} ${player.last_name || ""}`.trim()
                  : "";
                setForm((prev) => ({
                  ...prev,
                  player_id: pid,
                  label: prev.label && prev.label.trim() ? prev.label : derivedLabel,
                }));
              }}
              data-testid="helpers-form-player"
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] focus:outline-none px-3 py-2 text-sm"
            >
              <option value="">— No player link —</option>
              {sortedPlayers.map((pl) => {
                const nm = `${pl.first_name || ""} ${pl.last_name || ""}`.trim() || "(unnamed player)";
                const identifier = pl.booking_id ? ` · #${pl.booking_id}` : "";
                return (
                  <option key={pl.id} value={pl.id}>{nm}{identifier}</option>
                );
              })}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#525252] block mb-1">Role</label>
            <HelperRolePicker
              value={form.role}
              onChange={(role) => setForm({ ...form, role })}
              roles={CREATE_ROLES}
              roleMeta={ROLE_META}
              testId="helpers-form-role"
            />
            <p className="text-[10px] text-[#525252] mt-1">{ROLE_META[form.role].scope}</p>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-[#525252] block mb-1">Shift length</label>
            <select
              value={form.hours}
              onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })}
              data-testid="helpers-form-hours"
              className="w-full bg-[#0A0A0A] border border-[#262626] focus:border-[#CCFF00] focus:outline-none px-3 py-2 text-sm"
            >
              {SHIFT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {/* Iter373 · Fix 2 — Devices allowed input stays hidden
                 for organisers/deputies. Cap defaults to the
                 festival's device_limit (default 1). Rally-Lab
                 platform owner adjusts the limit from the Festival
                 Management panel on the festival page. */}
            <p className="text-[10px] text-[#525252] mt-1" data-testid="helpers-form-devices-note">
              Devices allowed per pass follow the festival's owner-set limit (default 1).
            </p>
          </div>
        </div>
        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={createPass}
            disabled={busy || !form.label.trim() || !licenseActive || isReadOnly}
            data-testid="helpers-form-submit"
            title={isReadOnly ? "Festival ended — read-only" : (!licenseActive ? "Activate the festival to unlock helper passes" : undefined)}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#CCFF00] text-black hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Plus className="w-4 h-4" /> {busy ? "Creating…" : licenseActive ? "Create pass" : "Locked — activate to unlock"}
          </button>
        </div>
      </div>

      {/* Fresh-pass reveal card (code shown only once) */}
      {freshPass && (
        <div className="card-surface p-4 mb-6 border-2 border-[#CCFF00]" data-testid="helpers-fresh-pass">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="bg-white p-3 shrink-0">
              <QRCodeSVG value={`${backendOrigin}${freshPass.redeem_url}?code=${freshPass.code}`} size={320} level="H" includeMargin={false} shapeRendering="crispEdges" style={{ width: 160, height: 160, imageRendering: "pixelated" }} />
            </div>
            <div className="flex-1 min-w-[240px]">
              <div className="label-mono text-[#CCFF00] mb-1">SHOW THIS ONCE</div>
              <h2 className="h-display text-xl leading-tight">{freshPass.label}</h2>
              <div className="text-[10px] uppercase tracking-widest text-[#525252] mb-3">
                {ROLE_META[freshPass.role]?.label} · expires {new Date(freshPass.expires_at).toLocaleString()}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">Access code</div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-3xl font-mono tracking-[0.3em] text-[#CCFF00]" data-testid="helpers-fresh-code">{freshPass.code}</span>
                <button type="button" onClick={() => copyText(freshPass.code)} className="p-1 text-[#A3A3A3] hover:text-[#CCFF00]" title="Copy code">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] mb-1">Redeem link</div>
              <div className="flex items-center gap-2 mb-3">
                <code className="text-[11px] text-[#A3A3A3] break-all">{`${backendOrigin}${freshPass.redeem_url}?code=${freshPass.code}`}</code>
                <button type="button" onClick={() => copyText(`${backendOrigin}${freshPass.redeem_url}?code=${freshPass.code}`)} className="p-1 text-[#A3A3A3] hover:text-[#CCFF00]" title="Copy link">
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <button
                type="button"
                onClick={() => setFreshPass(null)}
                data-testid="helpers-fresh-dismiss"
                className="text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-white"
              >
                I've shared it — dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Iter371 · Fix 3 — Buy-extra-slot modal. Opens from the
           Purchase Summary role cards. Posts a single-line
           basket-checkout with the matching bolt-on pass_type; the
           webhook / status-poll fulfilment increments
           helper_seats_bought.{role} so the quota lifts once the
           helper redirects back from Stripe. */}
      {buyRole && (
        <div
          className="fixed inset-0 z-[90] bg-black/70 flex items-center justify-center p-4"
          onClick={() => !buyBusy && closeBuyModal()}
          data-testid="helpers-buy-modal"
        >
          <div
            className="w-full max-w-md bg-[#0A0A0A] border-2 border-[#CCFF00] p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <div className="label-mono text-[#CCFF00] text-[10px]">ADD EXTRA HELPER SLOT</div>
                <h2 className="h-display text-xl mt-1">
                  {ROLE_META[buyRole]?.label || buyRole}
                </h2>
                <p className="text-xs text-[#A3A3A3] mt-1">
                  {ROLE_META[buyRole]?.scope}
                </p>
              </div>
              <button
                type="button"
                onClick={closeBuyModal}
                data-testid="helpers-buy-close"
                className="text-[#A3A3A3] hover:text-white"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="border border-[#262626] p-3 mb-3">
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm text-white">
                    {buyDef?.label || "Extra helper seat"}
                  </div>
                  <div className="text-[10px] text-[#A3A3A3] mt-0.5" data-testid="helpers-buy-unit-price">
                    {buyCanBuy
                      ? `${formatMoney(buyUnitAmount, buyCurrency)} each`
                      : "POA — contact us"}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => setBuyQty((q) => Math.max(1, Number(q) - 1))}
                    disabled={!buyCanBuy || buyQty <= 1}
                    data-testid="helpers-buy-minus"
                    className="w-7 h-7 border border-[#262626] text-white hover:border-[#CCFF00] hover:text-[#CCFF00] disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Decrease"
                  >
                    −
                  </button>
                  <span data-testid="helpers-buy-qty" className="w-8 text-center text-sm text-white">
                    {buyQty}
                  </span>
                  <button
                    type="button"
                    onClick={() => setBuyQty((q) => Math.min(10, Number(q) + 1))}
                    disabled={!buyCanBuy || buyQty >= 10}
                    data-testid="helpers-buy-plus"
                    className="w-7 h-7 border border-[#262626] text-white hover:border-[#CCFF00] hover:text-[#CCFF00] disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Increase"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
            <div className="border border-[#525252] bg-[#0F0F0F] p-3 mb-3">
              <div className="label-mono text-[10px] text-[#A3A3A3] mb-1">ORDER SUMMARY</div>
              <div className="text-xs text-white flex justify-between mb-1">
                <span>{buyDef?.label || "Extra slot"} × {buyQty}</span>
                <span>{formatMoney(buyTotal, buyCurrency)}</span>
              </div>
              <div className="text-sm font-bold text-[#CCFF00] flex justify-between border-t border-[#262626] pt-2 mt-2" data-testid="helpers-buy-total">
                <span>Total</span>
                <span>{formatMoney(buyTotal, buyCurrency)}</span>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeBuyModal}
                disabled={buyBusy}
                data-testid="helpers-buy-cancel"
                className="px-3 py-2 text-[10px] uppercase tracking-widest border border-[#525252] text-[#A3A3A3] hover:border-white hover:text-white disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitBuy}
                disabled={buyBusy || !buyCanBuy}
                data-testid="helpers-buy-submit"
                className="inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#CCFF00] text-black hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ShoppingCart className="w-4 h-4" /> {buyBusy ? "Redirecting…" : "Continue to payment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Existing passes */}
      <div className="card-surface" data-testid="helpers-passes-list">
        {passes.length === 0 ? (
          <div className="p-6 text-center text-sm text-[#525252] italic" data-testid="helpers-empty">
            No passes yet — create one above to bring a helper on shift.
          </div>
        ) : (
          <>
          <ListControlsBar ctl={passCtl} modes={["default", "az", "za"]} placeholder="Search helpers…" testid="helpers" className="p-3" />
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-[#525252] border-b border-[#262626]">
                <th className="text-left p-3">Label</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Expires</th>
                <th className="text-left p-3">Usage</th>
                <th className="p-3" />
              </tr>
            </thead>
            <tbody>
              {passCtl.list.map((p) => {
                const isExpired = new Date(p.expires_at) < new Date();
                const isRevoked = !!p.revoked_at;
                const isLocked = !!p.verify_locked_at;
                const state = isRevoked ? "revoked" : isExpired ? "expired" : isLocked ? "locked" : "active";
                const stateColor = state === "active" ? "text-[#CCFF00]" : state === "expired" ? "text-[#FFB020]" : "text-[#FF4500]";
                const cap = p.max_concurrent_redeems == null ? (Number(festival?.device_limits_by_role?.[p.role]) || 1) : Number(p.max_concurrent_redeems);
                const capUnlimited = false;
                const activeDevices = Number(p.active_devices || 0);
                const distinctDevices = Number(p.distinct_devices || 0);
                const overCap = activeDevices > cap;
                const lastRedeem = p.last_redeemed_at ? new Date(p.last_redeemed_at) : null;
                return (
                  <tr key={p.id} data-testid={`helpers-pass-${p.id}`} data-state={state} className="border-b border-[#141414] align-top">
                    <td className="p-3 relative">
                      {(() => {
                        // Iter326 · Bug 6 — Hover reveal for the fresh code
                        // + QR after a brute-force reset or "Reset older".
                        // The label becomes hover-hinted (dashed underline)
                        // only when we actually have a cached fresh code
                        // for this pass.
                        // Iter371 · Fix 2 — Always show a shift-pass QR
                        // on hover for non-revoked rows (active and
                        // expired). Fresh-code overlay wins when we
                        // have a cached rotated code (post-reset)
                        // because that includes the one-time access
                        // code too; otherwise the plain shift-pass
                        // QR is enough for check-in helpers.
                        const fresh = readFreshCode(p.id);
                        const hasFresh = !!(fresh && fresh.code);
                        const qrEnabled = !isRevoked;
                        // Iter374 · Fix 3 — Prefer the cached fresh code
                        // (post-reset) but fall back to the always-live
                        // `access_code` served in the summary so the QR
                        // is scannable in one shot and the code stays
                        // visible inline in the Role column.
                        const currentCode = hasFresh ? fresh.code : (p.access_code || null);
                        const redeemUrl = currentCode
                          ? `${backendOrigin}/festival/${id}/helper/${p.id}?code=${currentCode}`
                          : `${window.location.origin}/festival/${id}/helper/${p.id}`;
                        const showFresh = hasFresh && hoveredCodePid === p.id;
                        const showQr = !showFresh && qrEnabled && hoveredQrPid === p.id;
                        return (
                          <>
                            <div
                              className={`font-medium inline-block ${(hasFresh || qrEnabled) ? "cursor-help border-b border-dashed border-[#CCFF00]" : ""}`}
                              onMouseEnter={() => {
                                if (hasFresh) setHoveredCodePid(p.id);
                                else if (qrEnabled) setHoveredQrPid(p.id);
                              }}
                              onMouseLeave={() => {
                                setHoveredCodePid((cur) => (cur === p.id ? null : cur));
                                setHoveredQrPid((cur) => (cur === p.id ? null : cur));
                              }}
                              data-testid={`helpers-label-${p.id}`}
                              data-has-fresh-code={hasFresh ? "1" : "0"}
                              data-qr-enabled={qrEnabled ? "1" : "0"}
                            >
                              {p.label}
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-[#525252]">{ROLE_META[p.role]?.scope}</div>
                            {showFresh && (
                              <div
                                className="fixed z-[70] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#0A0A0A] border-2 border-[#CCFF00] p-4 shadow-2xl pointer-events-auto"
                                data-testid={`helpers-fresh-hover-${p.id}`}
                                onMouseEnter={() => setHoveredCodePid(p.id)}
                                onMouseLeave={() => setHoveredCodePid(null)}
                              >
                                <div className="label-mono text-[#CCFF00] text-[10px] mb-2">UPDATED ACCESS CODE</div>
                                <div className="bg-white p-2">
                                  <QRCodeSVG value={redeemUrl} size={320} level="H" includeMargin={false} shapeRendering="crispEdges" style={{ width: 240, height: 240, imageRendering: "pixelated" }} />
                                </div>
                                <div className="flex items-center gap-2 mt-2 justify-center">
                                  <span className="text-lg font-mono tracking-[0.25em] text-[#CCFF00]" data-testid={`helpers-fresh-hover-code-${p.id}`}>{fresh.code}</span>
                                  <button
                                    type="button"
                                    onClick={() => copyText(redeemUrl)}
                                    className="p-1 text-[#A3A3A3] hover:text-[#CCFF00]"
                                    title="Copy redeem link"
                                  >
                                    <Copy className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                                <div className="text-[10px] text-[#525252] mt-1 max-w-[240px] truncate mx-auto text-center" title={redeemUrl}>
                                  Cached from reset — hides on refresh
                                </div>
                              </div>
                            )}
                            {showQr && (
                              <div
                                className="fixed z-[70] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#0A0A0A] border-2 border-[#CCFF00] p-4 shadow-2xl pointer-events-auto"
                                data-testid={`helpers-qr-hover-${p.id}`}
                                onMouseEnter={() => setHoveredQrPid(p.id)}
                                onMouseLeave={() => setHoveredQrPid(null)}
                              >
                                <div className="label-mono text-[#CCFF00] text-[10px] mb-2">HELPER SHIFT PASS</div>
                                <div className="bg-white p-2">
                                  <QRCodeSVG value={redeemUrl} size={320} level="H" includeMargin={false} shapeRendering="crispEdges" style={{ width: 240, height: 240, imageRendering: "pixelated" }} />
                                </div>
                                {/* Iter374 · Fix 3 — Code shown inside
                                     the hover popup too so a helper
                                     who can't scan the QR still has
                                     the code visible next to the image. */}
                                {currentCode && (
                                  <div className="flex items-center gap-2 mt-2 justify-center" data-testid={`helpers-qr-code-${p.id}`}>
                                    <span className="text-lg font-mono tracking-[0.25em] text-[#CCFF00]">{currentCode}</span>
                                    <button
                                      type="button"
                                      onClick={() => copyText(redeemUrl)}
                                      className="p-1 text-[#A3A3A3] hover:text-[#CCFF00]"
                                      title="Copy redeem link"
                                    >
                                      <Copy className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                )}
                                <div className="text-[10px] text-[#A3A3A3] mt-2 max-w-[240px] truncate mx-auto text-center" title={redeemUrl}>
                                  {ROLE_META[p.role]?.label || p.role} · {p.label}
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] uppercase tracking-widest border px-2 py-0.5 self-start" style={{ borderColor: ROLE_META[p.role]?.color, color: ROLE_META[p.role]?.color }}>
                          {ROLE_META[p.role]?.label || p.role}
                        </span>
                        {/* Iter374 · Fix 3 — Access code always visible
                             inline next to the Role label. Non-revoked
                             passes only; revoked passes show a dash so
                             we don't leak stale codes. */}
                        {!isRevoked && p.access_code && (
                          <div
                            className="inline-flex items-center gap-1 text-[11px] font-mono tracking-[0.15em] text-[#CCFF00]"
                            data-testid={`helpers-access-code-${p.id}`}
                            title="Access code — helper types this after scanning their QR"
                          >
                            <KeyRound className="w-3 h-3" />
                            <span>{p.access_code}</span>
                            <button
                              type="button"
                              onClick={() => copyText(`${backendOrigin}/festival/${id}/helper/${p.id}?code=${p.access_code}`)}
                              className="p-0.5 text-[#A3A3A3] hover:text-[#CCFF00]"
                              data-testid={`helpers-access-code-copy-${p.id}`}
                              title="Copy redeem link (full URL)"
                            >
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className={`p-3 text-xs ${stateColor}`}>
                      {state === "active" && (<><Timer className="inline w-3 h-3 mr-1" />{fmtRemaining(p.expires_at)}</>)}
                      {state === "expired" && "Expired"}
                      {state === "revoked" && "Revoked"}
                      {state === "locked" && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#FF4500] bg-[#4B0F0F] text-[#FF4500]" data-testid={`helpers-locked-badge-${p.id}`}>
                          <KeyRound className="w-3 h-3" /> Locked — too many wrong codes
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-[#A3A3A3]" data-testid={`helpers-usage-${p.id}`}>
                      <div className="flex items-center gap-1 mb-0.5">
                        <Activity className="w-3 h-3" />
                        <span className={overCap ? "text-[#FF4500] font-semibold" : ""} data-testid={`helpers-active-${p.id}`}>
                          {activeDevices}/{cap} active device{cap === 1 ? "" : "s"}
                        </span>
                      </div>
                      <div className="text-[10px] text-[#525252]" data-testid={`helpers-redeemed-${p.id}`}>
                        {p.redeemed_count || 0} total redeems · {distinctDevices} distinct
                      </div>
                      {lastRedeem && (
                        <div className="text-[10px] text-[#525252]" title={lastRedeem.toISOString()}>
                          last: {lastRedeem.toLocaleString()}
                        </div>
                      )}
                      {overCap && (
                        <div className="text-[10px] text-[#FF4500] mt-0.5">Over cap — reset older to bump extras off</div>
                      )}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {state === "locked" && (
                        <button type="button" onClick={() => unlock(p.id)} data-testid={`helpers-unlock-${p.id}`} className="mr-2 text-[10px] uppercase tracking-widest text-[#FFB020] hover:text-white" title="Clear the brute-force lock so the helper can try again">
                          <KeyRound className="inline w-3.5 h-3.5" /> Reset lock
                        </button>
                      )}
                      {(state === "active" || state === "locked" || state === "expired") && (<>
                        {(state === "active" || state === "expired") && (
                          <>
                            {/* Iter367 · Fix 5 — Extend is now
                                 available on expired passes too. A
                                 mini dropdown surfaces 1h / 2h / 4h
                                 / 24h / until end of festival.
                                 Iter391 · Fix 7 — Wrapper is a
                                 `<div>` (was `<span>`) because the
                                 dropdown menu inside is block-level;
                                 nesting a `<div>` inside a `<span>`
                                 tripped React's hydration warning. */}
                            <div className="relative inline-block mr-2">
                              <button
                                type="button"
                                onClick={() => setExtendMenuPid((cur) => (cur === p.id ? null : p.id))}
                                data-testid={`helpers-extend-${p.id}`}
                                className={`text-[10px] uppercase tracking-widest ${state === "expired" ? "text-[#FFB020] hover:text-white" : "text-[#A3A3A3] hover:text-[#CCFF00]"}`}
                                title={state === "expired" ? "Renew this expired pass" : "Add more hours"}
                              >
                                <Clock className="inline w-3.5 h-3.5" /> {state === "expired" ? "Renew" : "Extend"}
                              </button>
                              {extendMenuPid === p.id && (
                                <div
                                  className="absolute right-0 top-full mt-1 z-40 bg-[#0A0A0A] border border-[#CCFF00] py-1 min-w-[180px] shadow-2xl"
                                  data-testid={`helpers-extend-menu-${p.id}`}
                                  onMouseLeave={() => setExtendMenuPid(null)}
                                >
                                  {[1, 2, 4, 24].map((h) => (
                                    <button
                                      key={h}
                                      type="button"
                                      onClick={() => { setExtendMenuPid(null); extend(p.id, h); }}
                                      data-testid={`helpers-extend-preset-${p.id}-${h}`}
                                      className="block w-full text-left px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#A3A3A3] hover:bg-[#141414] hover:text-[#CCFF00]"
                                    >
                                      + {h} hour{h === 1 ? "" : "s"}
                                    </button>
                                  ))}
                                  <button
                                    type="button"
                                    onClick={() => { setExtendMenuPid(null); extend(p.id, "until_end"); }}
                                    data-testid={`helpers-extend-preset-${p.id}-until-end`}
                                    className="block w-full text-left px-3 py-1.5 text-[11px] uppercase tracking-widest text-[#66C2FF] hover:bg-[#141414] hover:text-white border-t border-[#262626]"
                                  >
                                    Until end of festival
                                  </button>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                        {state === "active" && (
                          <>
                            {/* Iter372 · Fix 2 — Cap control is
                                 Rally-Lab platform owner only.
                                 Organisers and deputies never see
                                 this button; passes default to
                                 unlimited devices. */}
                            {isPlatformOwner && (
                              <button type="button" onClick={() => updateCap(p.id, cap)} data-testid={`helpers-cap-${p.id}`} className="mr-2 text-[10px] uppercase tracking-widest text-[#66C2FF] hover:text-[#CCFF00]" title="Rally-Lab owner: set / clear this pass's device cap">
                                <Users className="inline w-3.5 h-3.5" /> Cap
                              </button>
                            )}
                            <button type="button" onClick={() => revokeOlder(p.id)} data-testid={`helpers-revoke-older-${p.id}`} className="mr-2 text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-[#FFB020]" title="Sign every currently-redeemed device out; issue a fresh access code">
                              <RotateCcw className="inline w-3.5 h-3.5" /> Reset older
                            </button>
                          </>
                        )}
                        <button type="button" onClick={() => revoke(p.id)} data-testid={`helpers-revoke-${p.id}`} className="text-[10px] uppercase tracking-widest text-[#A3A3A3] hover:text-[#FF4500]" title="Revoke this pass immediately">
                          <Ban className="inline w-3.5 h-3.5" /> Revoke
                        </button>
                      </>)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </>
        )}
      </div>
    </div>
  );
}
